import { useMemo } from 'react';
import { 
  getDataSource, 
  type DataSourceType, 
  type UseDataSourceReturn,
  type ListWidgetItem,
  type TableDataOutput 
} from './dataSources/index';
import { useGlossaryDataContext } from "@/providers/ObjectsDataProvider";
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { useGlossaryBlueprintsDataContext } from '@/providers/GlossaryBlueprintsDataProvider';
import { useStringsDataContext } from '@/providers/StringsDataProvider';
import { useDocumentDataContext } from '@/providers/DocumentDataProvider';
import { useAuthStore } from './api-functions/api-auth';

export function useDataSource(sourceType: DataSourceType, selectedFolderId?: string | null): UseDataSourceReturn {
  const { glossaryItems, isLoading: glossaryLoading } = useGlossaryDataContext();
  const { games, companies, selectedGame, isLoading: userDataLoading } = useSimplifiedUserData();
  const { glossaryBlueprints, isLoading: blueprintsLoading } = useGlossaryBlueprintsDataContext();
  const { stringsItems, isLoading: stringsLoading } = useStringsDataContext();
  const { documents, isLoading: documentsLoading } = useDocumentDataContext();
  const { user } = useAuthStore();

  // Get the data source module from registry
  const dataSourceModule = useMemo(() => getDataSource(sourceType), [sourceType]);

  // Get the default language from the selected game
  const defaultLanguage = selectedGame?.settings?.defaultLanguage || "en";

  // Fetch raw data using the data source's fetchData function
  const rawData = useMemo(() => {
    try {
      switch (sourceType) {
        case 'objects':
        case 'glossary': // Deprecated: Use 'objects'
        case 'library': // Deprecated: Use 'objects'
          return dataSourceModule.fetchData(selectedGame, selectedFolderId);
        
        case 'documents':
          return dataSourceModule.fetchData(selectedGame, selectedFolderId);
        
        case 'templates':
        case 'glossary-blueprints': // Deprecated: Use 'templates'
          return dataSourceModule.fetchData(selectedGame, selectedFolderId);
        
        case 'strings':
          return dataSourceModule.fetchData(selectedGame, selectedFolderId, stringsItems);
        
        case 'dialogue-lines':
          return dataSourceModule.fetchData(selectedGame, selectedFolderId, stringsItems);
        
        case 'user-games':
          return dataSourceModule.fetchData(selectedGame, selectedFolderId, games);
        
        case 'user-companies':
          return dataSourceModule.fetchData(selectedGame, selectedFolderId, companies);
        
        case 'objects-sync':
        case 'glossary-sync': // Deprecated: Use 'objects-sync'
          return dataSourceModule.fetchData(selectedGame);
        
        case 'favorites':
          return dataSourceModule.fetchData(selectedGame, selectedFolderId, user);
        
        default:
          return [];
      }
    } catch (error) {
      console.error(`Error fetching data for source type "${sourceType}":`, error);
      return [];
    }
  }, [sourceType, selectedFolderId, selectedGame, games, companies, stringsItems, user, dataSourceModule]);

  // Transform data for list widget
  const transformedData = useMemo((): ListWidgetItem[] => {
    try {
      return dataSourceModule.transformers.toList(rawData);
    } catch (error) {
      console.error(`Error transforming data to list for source type "${sourceType}":`, error);
      return [];
    }
  }, [rawData, dataSourceModule, sourceType]);

  // Transform data for table widget with metadata
  const tableData = useMemo((): TableDataOutput => {
    try {
      return dataSourceModule.transformers.toTable(rawData, defaultLanguage);
    } catch (error) {
      console.error(`Error transforming data to table for source type "${sourceType}":`, error);
      return { columns: [], data: [], metadata: { totalCount: 0, filteredCount: 0 } };
    }
  }, [rawData, defaultLanguage, dataSourceModule, sourceType]);

  // Determine loading state based on source type
  const isLoading = useMemo(() => {
    switch (sourceType) {
      case 'objects':
      case 'glossary': // Deprecated
      case 'library': // Deprecated
        return glossaryLoading;
      case 'documents':
        return documentsLoading;
      case 'templates':
      case 'glossary-blueprints': // Deprecated
        return blueprintsLoading;
      case 'strings':
      case 'dialogue-lines':
        return stringsLoading;
      case 'user-games':
      case 'user-companies':
        return userDataLoading;
      case 'objects-sync':
      case 'glossary-sync': // Deprecated
        return glossaryLoading; // Uses same loading state as objects
      default:
        return false;
    }
  }, [sourceType, glossaryLoading, documentsLoading, blueprintsLoading, stringsLoading, userDataLoading]);

  return {
    data: rawData,
    transformedData,
    tableData,
    isLoading,
    error: null,
    metadata: {
      fields: dataSourceModule.fields,
      config: dataSourceModule.config
    }
  };
}
